import React, { useCallback, useEffect, useState } from 'react';
import { TextDetailsIA } from 'components/shared/textdetailsIA/textdetailsIA';
import SmartContentPopin from 'components/shared/SmartcontentModal/SmartContentPopin';
import TranslateContentPopin from 'components/shared/SmartcontentModal/TranslateContentPopin';
import TranslationSavedModal from 'components/shared/modal/TranslationSavedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import axiosPrivate from 'config/axiosPrivate';
import { useAdminClientSmartTextGeneration } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientSmartTextGeneration';
import { getAgentLanguageLabel, resolveLanguageDisplayLabel } from 'utils/smartContent/translationLanguageUtils';
import { useSmartTextTranslation } from 'utils/smartContent/useSmartTextTranslation';

const SmartProjectTextDetails = ({
  t,
  order,
  redactionData,
  keyword,
  url,
  titleTag,
  metaDescriptionTag,
  setKeyword,
  setUrl,
  setTitleTag,
  setMetaDescriptionTag,
  list,
  combinedKeywords,
  setList,
  setKeywords,
  setCombinedKeywords,
  websites,
  setWebsites,
  initialTextContent,
  initialHtmlContent,
  actualHtmlContent,
  actualTextContent,
  setActualHtmlContent,
  setActualTextContent,
  editorRef,
  expandMetaField = false,
  readOnly = false,
  onTranslationUiChange,
  onTranslationError,
}) => {
  const [projectSummary, setProjectSummary] = useState(null);
  const [isTranslatePopinOpen, setIsTranslatePopinOpen] = useState(false);

  useEffect(() => {
    const fetchProjectSummary = async () => {
      if (!redactionData?.id) return;
      try {
        const response = await axiosPrivate.get(
          `/texts/external/${redactionData.id}/project-summary`
        );
        setProjectSummary(response?.data || null);
      } catch (error) {
        console.error('Erreur API project-summary:', error);
      }
    };
    fetchProjectSummary();
  }, [redactionData?.id]);

  const smartGeneration = useAdminClientSmartTextGeneration({
    order,
    redactionData,
    projectSummary,
    keyword,
    url,
    titleTag,
    metaDescriptionTag,
    setKeyword,
    setUrl,
    setTitleTag,
    setMetaDescriptionTag,
    list,
    combinedKeywords,
    setList,
    setKeywords,
    setCombinedKeywords,
    websites,
    setWebsites,
    initialTextContent,
    initialHtmlContent,
    actualHtmlContent,
    actualTextContent,
    editorRef,
    onContentGenerated: ({ html, text }) => {
      if (html) setActualHtmlContent(html);
      if (text) setActualTextContent(text);
    },
  });

  const {
    contentConfig,
    contentTypeKey,
    isGenerating,
    isRegeneratingTitle,
    isRegeneratingMeta,
    isSettingsPopinOpen,
    setIsSettingsPopinOpen,
    isCreditsLimitModalOpen,
    setIsCreditsLimitModalOpen,
    remainingCredits,
    isCreditsExhausted,
    redactionAgentsList,
    availableAiModels,
    websiteUrl,
    gapAnalysisEnabled,
    translationEnabled,
    hasContentOrGenerated,
    selectedAgent,
    agentName,
    aiAgentModel,
    handleStartGeneration,
    handleRegenerateTitle,
    handleRegenerateMeta,
    handleSettingsSubmit,
    getPopinInitialValues,
    enrichmentLlmOptions,
    refreshCompanyQuotas,
  } = smartGeneration;

  const translation = useSmartTextTranslation({
    t,
    refreshCompanyQuotas,
    onCreditsLimit: () => setIsCreditsLimitModalOpen(true),
    onError: onTranslationError,
  });

  const sourceEditorContent = actualHtmlContent || actualTextContent || '';

  const handleTranslateSubmit = async (payload) => {
    setIsTranslatePopinOpen(false);
    await translation.handleStartTranslation(payload, {
      editorContent: sourceEditorContent,
      keyword,
      url,
      remainingCredits,
    });
  };

  const handleSaveTranslationClick = useCallback(() => {
    translation.handleSaveTranslation({
      editorContent: sourceEditorContent,
      keyword,
      url,
      titleTag,
      metaDescriptionTag,
      websites,
      websiteId: order?.website?.id || redactionData?.websiteId || 0,
    });
  }, [
    translation.handleSaveTranslation,
    sourceEditorContent,
    keyword,
    url,
    titleTag,
    metaDescriptionTag,
    websites,
    order?.website?.id,
    redactionData?.websiteId,
  ]);

  useEffect(() => {
    if (typeof onTranslationUiChange !== 'function') return;

    onTranslationUiChange({
      isTranslationMode: translation.isTranslationMode,
      isTranslating: translation.isTranslating,
      translatedContent: translation.translatedContent,
      setTranslatedContent: translation.setTranslatedContent,
      translationTargetLanguage: translation.translationTargetLanguage,
      isSavingTranslation: translation.isSavingTranslation,
      onSaveTranslation: handleSaveTranslationClick,
      sourceLanguageLabel:
        getAgentLanguageLabel(selectedAgent)
        || resolveLanguageDisplayLabel(order?.website?.language),
    });
  }, [
    onTranslationUiChange,
    translation.isTranslationMode,
    translation.isTranslating,
    translation.translatedContent,
    translation.setTranslatedContent,
    translation.translationTargetLanguage,
    translation.isSavingTranslation,
    handleSaveTranslationClick,
    selectedAgent,
    order?.website?.language,
  ]);

  return (
    <>
      <TextDetailsIA
        t={t}
        expandMetaField={expandMetaField}
        formData={{ keyword, url, titleTag, metaDescriptionTag }}
        errors={{}}
        isFieldDisabled={() => readOnly}
        handleChange={(name) => (e) => {
          if (readOnly) return;
          const value = e?.target?.value ?? e;
          if (name === 'keyword') setKeyword(value);
          if (name === 'url') setUrl(value);
          if (name === 'titleTag') setTitleTag(value);
          if (name === 'metaDescriptionTag') setMetaDescriptionTag(value);
        }}
        handleBlur={() => () => {}}
        showSeoControls={contentConfig?.showSeoControls !== false}
        showUrlControls={contentConfig?.showUrlControls !== false}
        onRegenerateTitle={
          !readOnly && hasContentOrGenerated ? handleRegenerateTitle : undefined
        }
        onRegenerateMeta={
          !readOnly && hasContentOrGenerated ? handleRegenerateMeta : undefined
        }
        isRegeneratingTitle={isRegeneratingTitle}
        isRegeneratingMeta={isRegeneratingMeta}
        onStartGeneration={readOnly ? undefined : handleStartGeneration}
        isGenerating={isGenerating}
        hasContentOrGenerated={hasContentOrGenerated}
        isTranslationEnabled={!readOnly && translationEnabled}
        showGenerationActions={!readOnly}
        onOpenTranslationPopin={
          readOnly ? undefined : () => setIsTranslatePopinOpen(true)
        }
        onOpenGenerationSettings={
          readOnly ? undefined : () => setIsSettingsPopinOpen(true)
        }
        isCreditsExhausted={isCreditsExhausted}
        onCreditsLimitClick={() => setIsCreditsLimitModalOpen(true)}
        remainingCredits={remainingCredits}
        showCreditsBadge
        showContentScoreVideo={contentConfig?.service && redactionData?.type !== 'TRANSLATED'}
        selectedAgent={selectedAgent}
        aiAgentModel={aiAgentModel}
        agentName={agentName}
      />

      <SmartContentPopin
        isOpen={isSettingsPopinOpen}
        onClose={() => setIsSettingsPopinOpen(false)}
        agents={redactionAgentsList}
        llmOptions={
          contentConfig?.service === 'CONTENT_IMPROVEMENT'
            ? enrichmentLlmOptions
            : availableAiModels
        }
        isTargetUrlRequired={contentConfig?.service === 'CONTENT_IMPROVEMENT'}
        disableGenerateButton={isCreditsExhausted}
        gapAnalysisEnabled={gapAnalysisEnabled === true}
        showAdvancedSeoSection={
          contentConfig?.service !== 'GMB' && contentConfig?.service !== 'YOUTUBE'
        }
        showWritingAxis={contentConfig?.service !== 'YOUTUBE'}
        contentType={contentTypeKey}
        showAgentContentTypeInDropdown={
          contentConfig?.contentType === 'OTHER' ||
          contentConfig?.service === 'CONTENT_IMPROVEMENT'
        }
        lockLlm
        lockAgent
        onBlockedGenerateClick={() => {
          setIsSettingsPopinOpen(false);
          setIsCreditsLimitModalOpen(true);
        }}
        mode="edit"
        existingKnowledgeBaseFiles={
          Array.isArray(redactionData?.knowledgeBaseFiles)
            ? redactionData.knowledgeBaseFiles
            : undefined
        }
        initialValues={getPopinInitialValues()}
        onSubmit={handleSettingsSubmit}
      />

      <TranslateContentPopin
        isOpen={isTranslatePopinOpen}
        onClose={() => setIsTranslatePopinOpen(false)}
        websiteUrl={websiteUrl}
        onSubmit={handleTranslateSubmit}
      />

      <TranslationSavedModal
        isOpen={translation.isTranslationSaveModalOpen}
        onClose={translation.handleStayHere}
        onGoToTranslation={translation.handleGoToSavedTranslation}
        targetLanguageLabel={translation.translationTargetLanguage?.language || ''}
        keyword={keyword || ''}
        mode={translation.translationSaveModalMode}
      />

      <BuyCreditsModal
        open={isCreditsLimitModalOpen}
        onClose={() => setIsCreditsLimitModalOpen(false)}
      />
    </>
  );
};

export default SmartProjectTextDetails;
